import type { Message } from '@/contexts/ai-assistant-context'
import { getMessageText, getMessageTools, toLeadWording } from './chat-utils'

export type AssistantActivityState = 'idle' | 'thinking' | 'searching' | 'working' | 'replying' | 'waiting' | 'ready'

export interface AssistantActivity {
  state: AssistantActivityState
  label: string
}

export function getToolActivity(toolName: string): AssistantActivity {
  const [verb, ...subjectParts] = toolName.toLowerCase().split('_')
  const subject = toLeadWording(subjectParts.join(' ').trim())
  const label = (action: string) => subject ? `${action} ${subject}…` : `${action}…`

  switch (verb) {
    case 'search':
    case 'find':
    case 'lookup':
      return { state: 'searching', label: label('Searching') }
    case 'get':
    case 'fetch':
    case 'read':
    case 'load':
      return { state: 'working', label: label('Reading') }
    case 'list':
      return { state: 'working', label: label('Reviewing') }
    case 'create':
      return { state: 'working', label: label('Creating') }
    case 'draft':
      return { state: 'working', label: label('Drafting') }
    case 'update':
      return { state: 'working', label: label('Updating') }
    case 'send':
      return { state: 'working', label: label('Sending') }
    case 'convert':
      return { state: 'working', label: label('Converting') }
    default:
      return {
        state: 'working',
        label: `Working on ${toLeadWording(toolName.replaceAll('_', ' '))}…`,
      }
  }
}

export function getAssistantActivity(messages: Message[], isProcessing: boolean, isWaitingForInput = false, hasUnreadResponse = false): AssistantActivity {
  if (!isProcessing && isWaitingForInput) return { state: 'waiting', label: 'Needs your input' }
  if (!isProcessing && hasUnreadResponse) return { state: 'ready', label: 'Your answer is ready' }
  if (!isProcessing) return { state: 'idle', label: 'Ask Cortado AI' }

  const assistantMessage = [...messages].reverse().find((message) => message.role === 'assistant')
  const runningTool = [...getMessageTools(assistantMessage?.parts ?? [])]
    .reverse()
    .find((tool) => tool.status === 'running')

  if (runningTool) {
    return getToolActivity(runningTool.toolName)
  }

  if (assistantMessage && getMessageText(assistantMessage.parts) && assistantMessage.isStreaming) {
    return { state: 'replying', label: 'Replying…' }
  }

  return { state: 'thinking', label: 'Thinking…' }
}
